<template>
	<page-container :page="page" class="blog-itempage">
		<header>
			<div class="row">
				<div class="columns column12">
					<h4 v-if="page.date" class="subtitle">
						{{ $d(new Date(page.date.date.split(' ')[0]), 'short') }}
					</h4>
					<h1>{{ page.header }}</h1>
					<p>{{ page.contentShort }}</p>
				</div>
			</div>
		</header>

		<section class="blog-image">
			<div class="row">
				<div class="columns column12">
					<picture>
						<source :srcset="page.imageWebp" type="image/webp" />
						<source :srcset="page.image" />
						<img :src="page.image" :alt="page.imageAlt" />
					</picture>
				</div>
			</div>
		</section>

		<section class="main-content blog-post-content">
			<div class="row">
				<div class="columns column12">
					<h4 class="subtitle">
						{{ page.header }}
					</h4>
					<div v-parse-links v-html="page.content" />
				</div>
			</div>
		</section>

		<section v-if="other" class="main-content">
			<div class="row">
				<div class="columns column12 align-center">
					<h2>{{ $t('nextBlog') }}</h2>
				</div>
			</div>
			<div class="row blog-item">
				<div class="columns column6 left">
					<h4 v-if="page.date" class="subtitle">
						{{ $d(new Date(page.date.date.split(' ')[0]), 'short') }}
					</h4>
					<h3>{{ other.header }}</h3>
					<p>{{ other.contentShort }}</p>
					<button-container>
						<nuxt-link :to="localePath(`/${other.filename}`)" class="button-arrow">
							{{ $t('viewNews') }}
						</nuxt-link>
					</button-container>
				</div>
				<div class="columns column6 right">
					<picture>
						<source :srcset="other.imageOverviewWebp" type="image/webp" />
						<source :srcset="other.imageOverview" />
						<img :src="other.imageOverview" :alt="other.imageOverviewAlt" loading="lazy" />
					</picture>
				</div>
			</div>
		</section>

		<contentblocks v-if="page.contentblocks && page.contentblocks.length" :items="page.contentblocks" />

		<review-slider v-if="page.showReviewSlider" :header="defaults[locale].website.reviewsHeader" />

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>

		<newsletter
			v-if="page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();
await fetchDefaults();

const { data: news } = await useWebsiteFetch('news', {
	query: { language: locale.value },
	key: `${locale.value}/news`,
});

const other = news.value.find((newsPage) => newsPage.pageID !== page.value.pageID);
</script>

<style lang="scss" scoped>
header {
	padding: 75px 0;
	margin-top: 130px;
	color: #fff;
	background: var(--footer-background-color);

	.row {
		max-width: 800px;
	}

	.subtitle {
		color: #fff;
	}

	.socials a {
		border-color: #a2a2a2;
		color: #fff;

		&:hover {
			border-color: #fff;
		}
	}
}

.blog-item {
	margin: 50px auto 100px;

	&:last-child {
		margin-bottom: 25px;
	}
}

.blog-image {
	margin: 0;
	position: relative;
	z-index: 2;
}

.blog-post-content .row {
	max-width: 800px;
}

:deep(blockquote) {
	position: relative;
	padding: 20px 0 40px 100px;

	p {
		font-family: var(--heading-font-family);
		font-size: 29px;
		line-height: 40px;
	}
}

:deep(blockquote::before) {
	content: '';
	display: inline-block;
	width: 64px;
	height: 48px;
	background: url('~/assets/images/reviews-quotes.png');
	position: absolute;
	top: 44px;
	left: 0;
}

@media (max-width: 960px) {
	.blog-item .columns {
		padding: 20px 40px;
	}
}
</style>
